import { Callout } from "$components/callout";
import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import {
  NetworkStatus,
  useNetworkStatus,
} from "$global-providers/network-status-provider/provider";
import {
  type Consultation,
  RXDB_CONSULTATION_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/consultation";
import {
  type Disease,
  RXDB_DISEASE_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/disease";
import {
  type Dispensation,
  RXDB_DISPENSATION_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/dispensation";
import {
  RXDB_INVESTIGATION_ACTIVITY_COLLECTION_NAME,
  type InvestigationActivity as RxInvestigationActivity,
} from "$global-providers/rxdb-provider/collections/investigation-activity";
import type { Patient } from "$global-providers/rxdb-provider/collections/patient";
import {
  type Prescription,
  RXDB_PRESCRIPTION_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/prescription";
import {
  RXDB_SYMPTOM_COLLECTION_NAME,
  type Symptom as RxSymptom,
} from "$global-providers/rxdb-provider/collections/symptom";
import {
  RXDB_VMP_COLLECTION_NAME,
  type Vmp,
} from "$global-providers/rxdb-provider/collections/vmp";
import { toast } from "$global-providers/toast-provider";
import { usePatientHistoryFiles } from "$routes/patient/[:patientId]/_lib/patient-history-file-upload";
import { CLINIC_QUERY } from "$routes/patient/[:patientId]/_lib/patient-queries";
import { RecommendedVaccinesCard } from "$routes/patient/[:patientId]/_lib/recommended-vaccines-card";
import { useAncEncountersQuery } from "$routes/patient/[:patientId]/antenatal-care/_lib/__generated__/anc-encounters.gql";
import type { HistoricAncEncounter } from "$routes/patient/[:patientId]/antenatal-care/_lib/anc-encounters";
import { useElephant200AdditionsFlag } from "$routes/patient/[:patientId]/antenatal-care/_lib/use-elephant-2-00-additions-flag";
import { linkToPatientANCEncounter } from "$routes/patient/[:patientId]/antenatal-care/links";
import { useOngoingInpatientVisitsQuery } from "$routes/patient/[:patientId]/inpatient/_lib/__generated__/ongoing-inpatient-visits.gql";
import { CurrentInpatientCard } from "$routes/patient/[:patientId]/inpatient/_lib/activity-card/current-inpatient-card";
import { inpatientGraphql } from "$routes/patient/[:patientId]/inpatient/_lib/queries";
import { usePatientEpocusEncounters } from "$routes/patient/[:patientId]/overview/history/_lib/use-patient-epocus-encounters";
import { usePatientImagingStudies } from "$routes/patient/[:patientId]/overview/history/_lib/use-patient-imaging-studies";
import { usePatientReferrals } from "$routes/patient/[:patientId]/overview/history/_lib/use-patient-referrals";
import { gql, useLazyQuery } from "@apollo/client";
import {
  Chip,
  CircularProgress,
  Link,
  styled,
  Typography,
} from "@material-ui/core";
import ErrorIcon from "@material-ui/icons/Error";
import { parse } from "date-fns";
import { AncEncounterStatus, type Clinic, type Gender } from "elephant-web/gql";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link as RouterLink } from "react-router-dom";
import { useRxData } from "rxdb-hooks";
import {
  useInpatientHistoryQuery,
  useOnlinePatientHistoryDetailsQuery,
  useUsersByIdQuery,
} from "./__generated__/history.gql";
import { HistoricEventTimeline } from "./historic-event-timeline";
import { isAncEncounter } from "./types";
import { usePatientHistoryReplicationState } from "./use-patient-history-replication-state";

gql`
  query InpatientHistory($input: PastInpatientVisitsInput!) {
    pastInpatientVisits(input: $input) {
      items {
        _id
        admissionEvent {
          _id
          type
          created
          notes
          performer {
            _id
            name
          }
          ...CareEventDiagnosis
        }
        dischargeEvent {
          _id
          type
          created
          notes
          performer {
            _id
            name
          }
          ...CareEventDiagnosis
        }
        meta {
          created
        }
      }
      hasMore
      limit
      offset
      total
    }
  }
  ${inpatientGraphql.fragments.careEventDiagnosisFragment}
`;

// TODO: ENG-4020 Drop the passportId field from the patientByCardId query
gql`
  query OnlinePatientHistoryDetails($id: String!) {
    patientByCardId(uuid: $id) {
      passportId
      patient {
        currentVisit {
          ...PatientCurrentVisit
        }
        card {
          id
        }
      }
    }
  }
`;

gql`
  query usersById($ids: [String]!) {
    usersById(ids: $ids) {
      _id
      person {
        firstname
        lastname
      }
    }
  }
`;

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(6),
}));

const FilterChipRow = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

export const History: FC<{
  patient: Patient;
  isSideBar?: boolean;
}> = ({ patient, isSideBar = false }) => {
  const [vaccineNudgesFlag] = useFeatureFlag(featureFlags.immunisations.nudges);
  const elephant200AdditionsFlag = useElephant200AdditionsFlag();
  const { formatMessage } = useIntl();
  const [ancOnly, setAncOnly] = useState(false);
  const isOffline = useNetworkStatus().networkStatus === NetworkStatus.OFFLINE;

  const {
    isPulling: isPullingHistory,
    failedToPull: failedToPullHistory,
    lastPulledAt: lastPulledHistoryAt,
  } = usePatientHistoryReplicationState();

  const { data: onlinePatientHistoryData } =
    useOnlinePatientHistoryDetailsQuery({
      variables: { id: patient.id },
    });

  // TODO: Remove this - is only used to show the clinic name on the prescription instructions
  // and also on the printable doctors report.
  // Consequently, is actually wrong. It should not be the clinic the patient was first
  // registered at, but instead the clinic that the instructions/report is being printed at.
  const [
    getClinic,
    { data: clinicData, called: clinicCalled, error: clinicDataError },
  ] = useLazyQuery<{ clinic: Clinic }>(CLINIC_QUERY);
  useEffect(() => {
    if (!clinicCalled && patient?.facilityId) {
      getClinic({
        variables: {
          clinicId: patient.facilityId,
        },
      });
    }
  }, [clinicCalled, getClinic, patient?.facilityId]);

  const { data: inpatientData } = useInpatientHistoryQuery({
    variables: { input: { patientUuid: patient.id, offset: 0, limit: 20 } },
    skip: isOffline,
  });
  // Filter out legacy detention-only visits (visits without admissionEvent)
  const inpatientVisits = (
    inpatientData?.pastInpatientVisits?.items ?? []
  ).filter((visit) => visit.admissionEvent);

  const { data: ongoingInpatientData } = useOngoingInpatientVisitsQuery({
    variables: { input: { patientUuid: patient.id, offset: 0, limit: 20 } },
    skip: isOffline,
  });

  const { data: ancEncountersData } = useAncEncountersQuery({
    variables: {
      input: {
        params: {
          patientId: patient.id,
          searchWholeTerritory: true,
        },
      },
    },
    skip: isOffline,
  });

  const encounters: HistoricAncEncounter[] = (
    ancEncountersData?.ancEncounters?.encounters ?? []
  ).map((encounter) => ({
    ...encounter,
    meta: { created: encounter.createdAt },
  }));

  const unfinalisedAncEncounters = encounters
    .filter((e) => e.status === AncEncounterStatus.InProgress)
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

  const { result: rxConsultationsData } = useRxData<Consultation>(
    RXDB_CONSULTATION_COLLECTION_NAME,
    (collection) =>
      collection.find({
        selector: {
          patientId: {
            $eq: patient.id,
          },
        },
        sort: [{ "meta.updatedAt": "desc" }],
        limit: 100,
      }),
  );

  const { result: rxDiseaseData } = useRxData<Disease>(
    RXDB_DISEASE_COLLECTION_NAME,
    (collection) =>
      collection.findByIds(
        rxConsultationsData.flatMap((consultation) => consultation.diseaseIds),
      ),
  );

  const { result: symptomsResult, isFetching: isFetchingRxdbSymptoms } =
    useRxData<RxSymptom>(RXDB_SYMPTOM_COLLECTION_NAME, (collection) =>
      collection.find(),
    );

  const { data: rxConsultationUserData, loading: rxConsultationUsersLoading } =
    useUsersByIdQuery({
      variables: {
        ids: rxConsultationsData.map((c) => c.meta.createdByUserId),
      },
      skip: rxConsultationsData.length === 0,
      fetchPolicy: "cache-and-network",
    });
  // TODO: refactor the consultation card component to more directly take the
  // syncable consultation type rather than mapping onto the legacy type here
  // (done here to save time)
  const symptomIdToLabelMap = useMemo(
    () =>
      isFetchingRxdbSymptoms
        ? null
        : symptomsResult.reduce(
            (acc, symptom) =>
              Object.assign(acc, { [symptom._data.id]: symptom._data.name }),
            {} as { [id: string]: string },
          ),
    [isFetchingRxdbSymptoms, symptomsResult],
  );

  const consultations = useMemo(() => {
    const diseaseMap = rxDiseaseData.reduce((acc, disease) => {
      acc[disease.id] = {
        displayName: disease.i18n.find((obj) => obj.locale === "en")
          .displayName,
        id: disease.id,
      };
      return acc;
    }, {});
    const userMap: Record<
      string,
      (typeof rxConsultationUserData)["usersById"][0]
    > = rxConsultationUserData?.usersById?.reduce((acc, user) => {
      acc[user._id] = user;
      return acc;
    }, {});
    return rxConsultationsData.map((consultation) => {
      const diseases = consultation.diseaseIds
        .map((diseaseId) => diseaseMap[diseaseId])
        .filter(Boolean);

      const deliveredBy = rxConsultationUsersLoading
        ? undefined
        : userMap?.[consultation.meta.createdByUserId];

      return {
        uuid: consultation.id,
        diagnoses: consultation.freeTextDiagnoses.map(({ id, name }) => ({
          id,
          codification: { name },
        })),
        diseases,
        plan: consultation.plan,
        problem: consultation.problem,
        presentingComplaint: consultation.presentingComplaint,
        deliveredOn: new Date(consultation.meta.createdAt),
        symptoms: symptomIdToLabelMap
          ? consultation.symptomIds.map(
              (symptomId) => symptomIdToLabelMap[symptomId],
            )
          : [],
        deliveredBy,
        meta: {
          ...consultation.meta,
          created: consultation.meta.createdAt,
        },
        isImmunisation: consultation.isImmunisation,
        backdatedTo: consultation.backdatedTo,
        isPerformedAtOtherClinic: consultation.isPerformedAtOtherClinic,
      };
    });
  }, [
    rxConsultationsData,
    rxDiseaseData,
    rxConsultationUserData?.usersById,
    rxConsultationUsersLoading,
    symptomIdToLabelMap,
  ]);

  const rxInvestigationsData = useRxData<RxInvestigationActivity>(
    RXDB_INVESTIGATION_ACTIVITY_COLLECTION_NAME,
    (collection) =>
      collection.find({
        selector: {
          patientId: {
            $eq: patient.id,
          },
        },
        sort: [{ "meta.updatedAt": "desc" }],
        limit: 100,
      }),
  );

  const investigations =
    rxInvestigationsData.result.map(({ _data }) => ({
      ..._data,
      meta: { ..._data.meta, created: _data.meta.createdAt },
    })) ?? [];

  const rxPrescriptionsData = useRxData<Prescription>(
    RXDB_PRESCRIPTION_COLLECTION_NAME,
    (collection) =>
      collection.find({
        selector: {
          patientId: {
            $eq: patient.id,
          },
        },
        sort: [{ "meta.updatedAt": "desc" }],
        limit: 100,
      }),
  );

  const rxDispensationsData = useRxData<Dispensation>(
    RXDB_DISPENSATION_COLLECTION_NAME,
    (collection) =>
      collection.find({
        selector: {
          prescriptionId: {
            $in: rxPrescriptionsData.result.map((r) => r.id),
          },
        },
        sort: [{ "meta.updatedAt": "desc" }],
        limit: 100,
      }),
  );

  const prescriptions =
    rxPrescriptionsData.result?.map((prescriptionRow) => {
      const dispensations = rxDispensationsData.result
        ?.filter((r) => r.prescriptionId === prescriptionRow.id)
        .map((r) => r.toJSON());
      return {
        ...prescriptionRow._data,
        meta: {
          ...prescriptionRow.meta,
          created: prescriptionRow.meta.createdAt,
        },
        dispensations,
      };
    }) ?? [];

  const { studies: imagingStudies } = usePatientImagingStudies(patient.id);

  const referrals = usePatientReferrals(patient.id, imagingStudies);

  const { encounters: rawEpocusEncounters } = usePatientEpocusEncounters(
    patient.id,
  );
  const epocusEncounters = rawEpocusEncounters.map((enc) => ({
    ...enc,
    meta: { ...enc.meta, created: enc.date },
  }));

  const { result: prescriptionVmps } = useRxData<Vmp>(
    RXDB_VMP_COLLECTION_NAME,
    (collection) =>
      collection.findByIds(
        prescriptions
          .map((prescription) => prescription.clinicalProduct?.id)
          .filter((id) => !!id),
      ),
  );

  const [patientFileUploadFlag] = useFeatureFlag(
    featureFlags.patientFileUpload,
  );
  const { files: patientHistoryFiles } = usePatientHistoryFiles({
    patientId: patient.id,
  });

  useEffect(() => {
    if (clinicDataError) {
      toast.error(
        formatMessage(
          {
            id: "patientHistory.clinicData.fetch",
            defaultMessage: "Error fetching clinic data: {error}",
          },
          { error: clinicDataError.message },
        ),
      );
    }
  }, [clinicDataError, formatMessage]);

  const historicEvents = useMemo(() => {
    // Create a Set of ANC prescription IDs to avoid RxDB duplicates
    const ancPrescriptionIds = new Set(
      encounters.flatMap(
        (encounter) => encounter.prescriptions?.map((p) => p.id) || [],
      ),
    );

    // Filter out RxDB prescriptions that are already shown in ANC encounters
    const filteredPrescriptions = prescriptions.filter(
      (prescription) => !ancPrescriptionIds.has(prescription.id),
    );

    return [
      ...consultations,
      ...filteredPrescriptions,
      ...investigations,
      ...inpatientVisits,
      ...encounters,
      ...referrals,
      ...(patientFileUploadFlag ? patientHistoryFiles : []),
      ...imagingStudies,
      ...epocusEncounters,
    ].sort((a, b) => {
      return (
        new Date(
          "backdatedTo" in b && b.backdatedTo ? b.backdatedTo : b.meta.created,
        ).getTime() -
        new Date(
          "backdatedTo" in a && a.backdatedTo ? a.backdatedTo : a.meta.created,
        ).getTime()
      );
    });
  }, [
    consultations,
    prescriptions,
    investigations,
    inpatientVisits,
    encounters,
    referrals,
    patientHistoryFiles,
    patientFileUploadFlag,
    imagingStudies,
    epocusEncounters,
  ]);

  return (
    <>
      <SyncStatus
        isSyncing={isPullingHistory}
        lastSyncedAt={lastPulledHistoryAt}
        failedToSync={failedToPullHistory}
      />
      <Container>
        {elephant200AdditionsFlag && unfinalisedAncEncounters.length > 0 && (
          <UnfinalisedAncBanner
            patientUuid={patient.id}
            encounters={unfinalisedAncEncounters}
          />
        )}
        {(ongoingInpatientData?.ongoingInpatientVisits?.items ?? []).map(
          (visit) => (
            <CurrentInpatientCard
              patientId={patient.id}
              visitId={visit._id}
              data-testid="patient-details-current-inpatient-visit"
              key={visit._id}
            />
          ),
        )}
        {vaccineNudgesFlag && !isSideBar && <RecommendedVaccinesCard />}
        {elephant200AdditionsFlag && (
          <FilterChipRow>
            <Chip
              data-testid="patient.history.filter.ancOnly"
              label={formatMessage({
                id: "patient.history.filter.ancOnly",
                defaultMessage: "ANC only",
              })}
              clickable
              color={ancOnly ? "primary" : "default"}
              variant={ancOnly ? "default" : "outlined"}
              onClick={() => setAncOnly((v) => !v)}
              aria-pressed={ancOnly}
            />
          </FilterChipRow>
        )}
        <HistoricEventTimeline
          historicEvents={
            elephant200AdditionsFlag && ancOnly
              ? historicEvents.filter(isAncEncounter)
              : historicEvents
          }
          patient={
            patient
              ? {
                  name: [
                    patient.name.first,
                    patient.name.middle,
                    patient.name.last,
                  ]
                    .filter(Boolean)
                    .join(" "),
                  cardId:
                    onlinePatientHistoryData?.patientByCardId?.patient?.card
                      ?.id ??
                    onlinePatientHistoryData?.patientByCardId?.passportId,
                  uuid: patient.id,
                  gender: patient.gender as Gender,
                  dob: parse(patient.dob, "yyyy-MM-dd", new Date()),
                  phoneNumber: patient.phone?.number,
                }
              : undefined
          }
          facilityName={clinicData?.clinic.name ?? "CLINIC NOT FOUND"}
          prescriptionVmps={prescriptionVmps}
        />
      </Container>
    </>
  );
};

const BannerContainer = styled("div")(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const UnfinalisedAncBanner: FC<{
  patientUuid: string;
  encounters: HistoricAncEncounter[];
}> = ({ patientUuid, encounters }) => {
  const mostRecent = encounters[0];
  const encounterLink = `${linkToPatientANCEncounter(mostRecent.id)}?patientUuid=${encodeURIComponent(patientUuid)}`;
  return (
    <BannerContainer data-testid="patient.history.unfinalisedAncBanner">
      <Callout type="warning">
        <FormattedMessage
          id="patient.history.unfinalisedAnc.banner"
          defaultMessage="{count, plural, one {This patient has 1 ANC visit awaiting finalisation.} other {This patient has # ANC visits awaiting finalisation.}} {link}"
          values={{
            count: encounters.length,
            link: (
              <Link
                component={RouterLink}
                to={encounterLink}
                data-testid="patient.history.unfinalisedAncBanner.link"
              >
                <FormattedMessage
                  id="patient.history.unfinalisedAnc.banner.link"
                  defaultMessage="{count, plural, one {Open visit} other {Open most recent}}"
                  values={{ count: encounters.length }}
                />
              </Link>
            ),
          }}
        />
      </Callout>
    </BannerContainer>
  );
};

const SyncStatusContainer = styled("div")(() => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginTop: "16px",
  marginBottom: "16px",
  height: "16px",
}));
const SyncStatusText = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  color: theme.palette.grey[700],
  marginLeft: "8px",
}));

const StyledErrorIcon = styled(ErrorIcon)(({ theme }) => ({
  width: "14px",
  height: "14px",
  color: theme.palette.error.main,
}));

const SyncStatus: FC<{
  isSyncing: boolean;
  lastSyncedAt?: Date;
  failedToSync?: boolean;
}> = ({ isSyncing, failedToSync, lastSyncedAt }) => {
  return (
    <SyncStatusContainer>
      {isSyncing ? (
        <>
          <CircularProgress size={14} />
          <SyncStatusText>
            <FormattedMessage
              id="patient.history.syncStatus.syncing"
              defaultMessage="Updating patient history"
            />
          </SyncStatusText>
        </>
      ) : failedToSync ? (
        <>
          <StyledErrorIcon />
          <SyncStatusText>
            {lastSyncedAt ? (
              <FormattedMessage
                id="patient.history.syncStatus.failedWithLastSyncedAt"
                defaultMessage="Update failed. Last updated {date} at {time}"
                values={{
                  date: lastSyncedAt.toLocaleDateString("en-US", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  }),
                  time: lastSyncedAt.toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  }),
                }}
              />
            ) : (
              <FormattedMessage
                id="patient.history.syncStatus.failed"
                defaultMessage="Update failed"
              />
            )}
          </SyncStatusText>
        </>
      ) : null}
    </SyncStatusContainer>
  );
};
